import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
// Strips everything except letters/digits and lowercases, so matching is
// resilient to punctuation/spacing differences (e.g. "M.B.A." vs "mba").
const normalize = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

export default function Autocomplete({
  options = [],
  value,
  onChange,
  onSelect,
  placeholder = '',
  allowCustom = true,
  maxSuggestions = 6,
  className = '',
}) {
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const [customMode, setCustomMode] = useState(false); // true once user clicks "Other"
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null); // the portaled <ul>, lives outside wrapperRef in the DOM

  // Dropdown is rendered in a portal (see below) so it isn't clipped by
  // any ancestor with overflow:hidden / overflow:auto (e.g. a fixed-height
  // scrollable chat widget). Since it's portaled to <body>, we compute its
  // screen position manually and flip it above the input when there isn't
  // enough room below.
  const [dropdownStyle, setDropdownStyle] = useState(null);

  useEffect(() => {
    if (open && wrapperRef.current) {
      const win = wrapperRef.current.ownerDocument.defaultView || window;
      const rect = wrapperRef.current.getBoundingClientRect();
      const estimatedHeight = 240; // matches max-h-60
      const spaceBelow = win.innerHeight - rect.bottom;
      const openUpward = spaceBelow < estimatedHeight;

      setDropdownStyle({
        position: 'fixed',
        left: rect.left,
        width: rect.width,
        ...(openUpward
          ? { bottom: win.innerHeight - rect.top + 4 }
          : { top: rect.bottom + 4 }),
      });
    } else {
      setDropdownStyle(null);
    }
  }, [open, value]);

  // Recompute position on scroll/resize while open, since fixed
  // coordinates go stale if the page or chat panel scrolls.
  useEffect(() => {
    if (!open) return;
    const recompute = () => {
      if (!wrapperRef.current) return;
      const win = wrapperRef.current.ownerDocument.defaultView || window;
      const rect = wrapperRef.current.getBoundingClientRect();
      const estimatedHeight = 240;
      const spaceBelow = win.innerHeight - rect.bottom;
      const openUpward = spaceBelow < estimatedHeight;
      setDropdownStyle({
        position: 'fixed',
        left: rect.left,
        width: rect.width,
        ...(openUpward
          ? { bottom: win.innerHeight - rect.top + 4 }
          : { top: rect.bottom + 4 }),
      });
    };
    window.addEventListener('scroll', recompute, true);
    window.addEventListener('resize', recompute);
    return () => {
      window.removeEventListener('scroll', recompute, true);
      window.removeEventListener('resize', recompute);
    };
  }, [open]);

  // NOTE: the listeners above are attached to the top-level `window`.
  // If this component ever renders inside an <iframe>, also attach them
  // to `wrapperRef.current.ownerDocument.defaultView` so scrolling inside
  // the iframe itself also triggers a recompute.

  const query = (value || '').trim();
  const normQuery = normalize(query);

  const matches = normQuery
    ? options
        .filter((opt) => normalize(opt).includes(normQuery))
        // exact / starts-with matches float to the top
        .sort((a, b) => {
          const aStarts = normalize(a).startsWith(normQuery) ? 0 : 1;
          const bStarts = normalize(b).startsWith(normQuery) ? 0 : 1;
          return aStarts - bStarts;
        })
        .slice(0, maxSuggestions)
    : options.slice(0, maxSuggestions);

  // total selectable rows = matches + the "Other" row (if enabled)
  const rowCount = matches.length + (allowCustom ? 1 : 0);

  // close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      const insideWrapper = wrapperRef.current && wrapperRef.current.contains(e.target);
      const insideDropdown = dropdownRef.current && dropdownRef.current.contains(e.target);
      if (!insideWrapper && !insideDropdown) {
        setOpen(false);
        setHighlighted(-1);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const pick = (text) => {
    onSelect(text);
    setOpen(false);
    setHighlighted(-1);
    setCustomMode(false);
  };

  // Clicking "Other" doesn't set a value by itself — it just confirms
  // free typing is fine, closes the dropdown, and keeps focus so the
  // user can keep typing (or finish typing) their own entry.
  const chooseOther = () => {
    setOpen(false);
    setHighlighted(-1);
    setCustomMode(true);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (!open || rowCount === 0) {
      if (e.key === 'Enter' && allowCustom && value) {
        e.preventDefault();
        pick(value);
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted((h) => (h + 1) % rowCount);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((h) => (h - 1 + rowCount) % rowCount);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlighted >= 0 && highlighted < matches.length) {
        pick(matches[highlighted]);
      } else if (highlighted === matches.length && allowCustom) {
        chooseOther();
      } else if (allowCustom && value) {
        pick(value);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setHighlighted(-1);
    }
  };

  // Highlights the portion of `text` that matches the (normalized) query.
  // Because matching happens on a normalized string (punctuation/spaces
  // stripped), we walk the original string counting only alphanumeric
  // characters to map the normalized match position back to real indices.
  const highlightMatch = (text) => {
    if (!normQuery) return text;

    const normText = normalize(text);
    const normIdx = normText.indexOf(normQuery);
    if (normIdx === -1) return text;

    let count = 0;
    let start = -1;
    let end = -1;

    for (let i = 0; i < text.length; i++) {
      if (/[a-z0-9]/i.test(text[i])) {
        if (count === normIdx) start = i;
        count++;
        if (count === normIdx + normQuery.length) {
          end = i + 1;
          break;
        }
      }
    }

    if (start === -1) return text;
    if (end === -1) end = text.length;

    return (
      <>
        {text.slice(0, start)}
        <span className="font-semibold text-[#2563EB]">
          {text.slice(start, end)}
        </span>
        {text.slice(end)}
      </>
    );
  };

  return (
    <div ref={wrapperRef} className="relative">
      <input
        ref={inputRef}
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setHighlighted(-1);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        className={`border rounded-lg px-3 py-2 w-full ${className}`}
        autoComplete="off"
      />

      {open && (matches.length > 0 || allowCustom) && dropdownStyle &&
        createPortal(
          <ul
            ref={dropdownRef}
            style={{ ...dropdownStyle, zIndex: 9999 }}
            className="max-h-60 overflow-y-auto bg-white border rounded-lg shadow-lg"
          >
            {matches.map((option, i) => (
              <li
                key={option}
                onMouseDown={() => pick(option)}
                onMouseEnter={() => setHighlighted(i)}
                className={`px-3 py-2 text-sm cursor-pointer ${
                  i === highlighted ? 'bg-[#EFF6FF]' : 'hover:bg-gray-50'
                }`}
              >
                {highlightMatch(option)}
              </li>
            ))}

            {matches.length === 0 && (
              <li className="px-3 py-2 text-sm text-gray-400 select-none">
                No matches
              </li>
            )}

            {allowCustom && (
              <li
                onMouseDown={chooseOther}
                onMouseEnter={() => setHighlighted(matches.length)}
                className={`px-3 py-2 text-sm cursor-pointer border-t flex items-center gap-1.5 ${
                  highlighted === matches.length ? 'bg-[#EFF6FF]' : 'hover:bg-gray-50'
                }`}
              >
                <span className="text-[#2563EB] font-semibold">+ Other</span>
                {value ? (
                  <span className="text-gray-500">
                    — use "<span className="font-medium text-gray-700">{value}</span>"
                  </span>
                ) : (
                  <span className="text-gray-500">— type your own</span>
                )}
              </li>
            )}
          </ul>,
          wrapperRef.current ? wrapperRef.current.ownerDocument.body : document.body
        )}

      {customMode && !open && (
        <p className="text-xs text-gray-400 mt-1">
          Typing your own value — it'll be saved as entered.
        </p>
      )}
    </div>
  );
}